import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import api from '../lib/api';
import { Loader2, AlertCircle } from 'lucide-react';

/**
 * V13.14 — SSO landing page.
 * OPS-issued magic-link URL format: /sso?t=<token>&next=<encoded path>
 *
 * Flow: on mount → exchange token for JWT → store it → navigate to next.
 * One-shot: second visit with same token shows error.
 */
export default function SSOLanding() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setAuth } = useAuth();
  const [status, setStatus] = useState('exchanging');
  const [errorMsg, setErrorMsg] = useState('');
  const [needsOtp, setNeedsOtp] = useState(false);

  useEffect(() => {
    const token = params.get('t');
    const next = params.get('next') || '/learner/dashboard';
    if (!token) {
      setStatus('error');
      setErrorMsg('No token provided.');
      return;
    }
    (async () => {
      try {
        const res = await api.get(`/api/auth/sso-exchange?t=${encodeURIComponent(token)}`);
        const { access_token, user } = res.data;
        const otp = Boolean(res.data.needs_phone_verification || user?.needs_phone_verification);
        setNeedsOtp(otp);
        setAuth({ token: access_token, user });
        setStatus('ok');
        setTimeout(() => navigate(next), otp ? 1800 : 400);
      } catch (err) {
        const d = err.response?.data?.detail;
        setErrorMsg(d?.message || 'This link is invalid or has expired.');
        setStatus('error');
      }
    })();
  }, [params, navigate, setAuth]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6" data-testid="sso-landing">
      <div className="max-w-md w-full text-center space-y-4">
        {status === 'exchanging' && (
          <>
            <Loader2 className="h-10 w-10 mx-auto text-rose-500 animate-spin" />
            <h1 className="text-lg font-medium">Signing you in…</h1>
            <p className="text-sm text-muted-foreground">One moment.</p>
          </>
        )}
        {status === 'ok' && (
          <>
            <div className="h-10 w-10 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
              ✓
            </div>
            <h1 className="text-lg font-medium">Welcome back!</h1>
            {needsOtp ? (
              <p className="text-sm text-muted-foreground" data-testid="sso-otp-expectation">
                Payment received. Next: a quick one-time WhatsApp code to confirm your number, then your dashboard.
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">Redirecting…</p>
            )}
          </>
        )}
        {status === 'error' && (
          <>
            <AlertCircle className="h-10 w-10 mx-auto text-amber-500" />
            <h1 className="text-lg font-medium">Couldn't sign you in</h1>
            <p className="text-sm text-muted-foreground">{errorMsg}</p>
            <a href="/login" className="text-sm text-rose-600 underline">
              Log in with your email
            </a>
          </>
        )}
      </div>
    </div>
  );
}
